import {actionTool, navigationTool, noInput, objectInput, stringInput, waitForSecrets} from "@/functions/webMcpTool";
import {chooseOption, fillInput, findButtonByText, findElement, formatPhoneForInput, formatSsnForInput, labelFor, press, pressButtonWithText} from "@/functions/webMcpDom";
import getBrandName from "@/functions/getBrandName";

const PTIN_PATTERN = /^P\d{8}$/

const radioOptions = (formSelector) => [...document.querySelectorAll(`${formSelector} input[type="radio"]`)]
	.map((input) => ({id: input.id, label: labelFor(input)}))
	.filter((option) => option.label)

const chooseRadioByLabel = (formSelector, wanted, description) => {
	const target = String(wanted ?? '').replace(/\s+/g, ' ').trim().toLowerCase()
	const options = radioOptions(formSelector)
	const match = options.find((option) => option.label.toLowerCase() === target) ?? options.find((option) => option.label.toLowerCase().includes(target))
	if (!target || !match) {
		throw new Error(`"${wanted}" is not one of the ${description} on this page: ${options.map((option) => option.label).join('; ')}.`)
	}
	chooseOption(`[id=${JSON.stringify(match.id)}]`, description)
}

const choiceInput = (description, labels) => stringInput(description, labels.length ? {enum: labels} : {})

const looksLikePhone = (value) => !value.includes('@') && /^[\d\s().+-]+$/.test(value) && [10, 11].includes(value.replace(/\D/g, '').length)

const buildStartRecovery = () => actionTool({
	name: 'start_account_recovery',
	description: `Start recovering a ${getBrandName()} account the person can't sign in to, using the email address or phone number on the account. Use it on the account recovery page when the person has forgotten their username or password. Returns the next step, usually confirming identity with the account's Social Security number, or the page's message.`,
	inputSchema: objectInput({emailOrPhone: stringInput('The email address or 10-digit phone number on the account.', {maxLength: 128})}, ['emailOrPhone']),
	run: ({emailOrPhone}) => {
		const value = String(emailOrPhone ?? '').trim()
		fillInput('#emailOrPhone', looksLikePhone(value) ? formatPhoneForInput(value) : value, 'email or phone field')
		return press('button[type="submit"][form="emailOrUsernameForm"]', 'Continue button')
	},
})

const buildUseUsernameInstead = ({router}) => navigationTool({
	name: 'use_username_instead',
	description: 'Switch account recovery to use the account\'s username instead of an email address or phone number. Use it when the person knows their username but not the email or phone on the account. Opens the username step and returns it; then call start_account_recovery there with the username.',
	execute: () => router.push('/reclaim-with-username'),
})

const buildStartRecoveryWithUsername = () => actionTool({
	name: 'start_account_recovery',
	description: `Start recovering a ${getBrandName()} account using the username on it. Use it on the username recovery page when the person knows their username but can't sign in. Returns the next step, usually confirming identity with the account's Social Security number, or the page's message if the username isn't found.`,
	inputSchema: objectInput({username: stringInput('The username on the account.', {maxLength: 128})}, ['username']),
	run: ({username}) => {
		fillInput('#username', username, 'username field')
		return press('button[type="submit"][form="reclaimWithUsernameForm"]', 'Continue button')
	},
})

const buildVerifySsn = () => actionTool({
	name: 'verify_identity_with_ssn',
	description: 'Confirm the person\'s identity with the Social Security number on the account, exactly as the Continue button does, including the site\'s captcha. Returns the next step: choosing an account, choosing where to send a recovery code, or confirming an email or username. The number is never returned or logged.',
	inputSchema: objectInput({ssn: stringInput('The 9-digit Social Security number on the account, with or without dashes.', {maxLength: 11})}, ['ssn']),
	run: ({ssn}) => {
		fillInput('#ssn', formatSsnForInput(ssn), 'Social Security number field')
		return press('button[type="submit"][form="verifySsnForm"]', 'Continue button')
	},
})

const buildRecoverWithoutSsn = () => actionTool({
	name: 'recover_without_ssn',
	description: 'Recover an account that was created without a Social Security number, from the SSN step. It opens the "Why do you need my SSN" help and follows its link for accounts without an SSN. This only works when the account has no SSN saved; otherwise the site shows a message. Returns the next step or that message.',
	run: () => {
		pressButtonWithText('Why do you need my SSN to recover my account?')
		findElement('#recoveringYourAccountModal a[href="#"]', 'no-SSN recovery link').click()
	},
})

const buildVerifyPtin = () => actionTool({
	name: 'verify_identity_with_ptin',
	description: 'Confirm a tax preparer\'s identity with the Preparer Tax Identification Number on the account, on the PTIN step that preparer accounts reach during recovery. Submits exactly as the Continue button does, including the site\'s captcha, which only the person can solve if a challenge appears. Returns the next step. The PTIN is never returned or logged.',
	inputSchema: objectInput({ptin: stringInput('The PTIN on the account: the letter P and 8 digits, for example P12345678.', {maxLength: 9})}, ['ptin']),
	run: ({ptin}) => {
		const value = String(ptin ?? '').trim().toUpperCase()
		if (!PTIN_PATTERN.test(value)) {
			throw new Error('A PTIN is the letter P followed by 8 digits, for example P12345678.')
		}
		fillInput('#ptin', value, 'PTIN field')
		return press('button[type="submit"][form="verifyPtinForm"]', 'Continue button')
	},
})

const buildSelectAccount = () => {
	const usernames = radioOptions('#selectAccountForm').map((option) => option.label)
	return actionTool({
		name: 'select_account',
		description: 'Choose which account to recover when several matched what the person gave. Pick it by the username exactly as the page lists it, then continue. Returns the next step, choosing where to send a recovery code. If the person isn\'t sure which account is theirs, use email_account_details instead.',
		inputSchema: objectInput({username: choiceInput('The username of the account to recover, as listed on the page.', usernames)}, ['username']),
		run: ({username}) => {
			chooseRadioByLabel('#selectAccountForm', username, 'accounts')
			return press('button[type="submit"][form="selectAccountForm"]', 'Continue button')
		},
	})
}

const buildEmailAccountDetails = () => actionTool({
	name: 'email_account_details',
	description: 'Email the person more information about each account that matched, when they don\'t know which one to choose. Use it on the choose-an-account page. The email goes to the address on file, not to you; the page shows a message when it is sent, and the person can then pick the account with select_account.',
	run: () => pressButtonWithText('Email me more information about my accounts.'),
})

const buildSendResetCode = () => {
	const destinations = radioOptions('#selectReclaimMethodForm').map((option) => option.label)
	return actionTool({
		name: 'send_reset_code',
		description: 'Choose where to send the account recovery code, then send it. Phone numbers and email addresses are shown masked, exactly as the page lists them; pass one of those labels. Returns the next step, entering the code. The code goes to the person\'s phone or email, so ask them for it; it is never shown to you.',
		inputSchema: objectInput({destination: choiceInput('Where to send the code, as the page lists it, for example "Text code to (***) ***-1234".', destinations)}, ['destination']),
		run: ({destination}) => {
			chooseRadioByLabel('#selectReclaimMethodForm', destination, 'code destinations')
			return press('button[type="submit"][form="selectReclaimMethodForm"]', 'Continue button')
		},
	})
}

const buildSubmitResetCode = () => actionTool({
	name: 'submit_reset_code',
	description: 'Continue once the account recovery code the person received by text, call, or email is in the page. The person types it, or the browser fills it from the text message; it never passes through this tool, so it waits until the field is filled. Returns the next step, choosing a new password, or the page\'s message for a wrong or expired code.',
	inputSchema: noInput,
	run: () => waitForSecrets('submit_reset_code', ['#code']) ?? press('button[type="submit"][form="resetCodeForm"]', 'Continue button'),
})

const RESEND_OPTIONS = {call: 'Send new code by phone call', text: 'Send new code by text'}

const buildResendResetCode = () => {
	const available = Object.keys(RESEND_OPTIONS).filter((option) => findButtonByText(RESEND_OPTIONS[option]))
	if (!available.length) return []
	return [actionTool({
		name: 'resend_reset_code',
		description: `Send a new recovery code to the same phone number by ${available.map((option) => (option === 'call' ? 'phone call' : 'text')).join(' or ')}. The page shows a message when it's sent.`,
		inputSchema: objectInput({by: stringInput('How to send the new code.', {enum: available})}, ['by']),
		run: ({by}) => pressButtonWithText(RESEND_OPTIONS[by] ?? RESEND_OPTIONS[available[0]]),
	})]
}

const buildChooseNewPassword = () => actionTool({
	name: 'choose_new_password',
	description: 'Save the new password for the recovered account. It goes into both password fields from the person or their password manager, never through this tool, so it waits until both are filled. The site checks it against its password rules and a list of breached passwords, so a password can meet every rule on the page and still be refused. Returns the next step or the page\'s message.',
	inputSchema: noInput,
	run: () => waitForSecrets('choose_new_password', ['#password', '#confirmPassword']) ?? press('button[type="submit"][form="changePasswordForm"]', 'Continue button'),
})

const buildProvideRecoveryEmail = () => actionTool({
	name: 'provide_recovery_email',
	description: 'Enter the email address on the account so the site can confirm it\'s the person, during recovery without a Social Security number or when recovering a username. Returns the next step, or the message the page shows. When the site finds the account it may email the username to that address rather than show it to you.',
	inputSchema: objectInput({email: stringInput('The email address on the account.', {maxLength: 75})}, ['email']),
	run: ({email}) => {
		fillInput('#email', String(email ?? '').trim(), 'email field')
		return press('button[type="submit"][form="enterEmailForm"]', 'Continue button')
	},
})

const buildNoAccessToEmail = () => navigationTool({
	name: 'no_access_to_email',
	description: 'Tell the site the person can\'t get to the email address on the account, on the enter-email recovery step. The page shows a message with what to do next, usually contacting support; read it from the returned step. Nothing is sent and nothing on the account changes.',
	execute: () => pressButtonWithText('I don’t have access to my email'),
})

const buildProvideRecoveryUsername = () => actionTool({
	name: 'provide_recovery_username',
	description: 'Enter the username on the account so the site can confirm it\'s the person, on the enter-username recovery step. Returns the next step, or the message the page shows. When the site already has the account\'s email address, it may continue straight to a verification code instead of asking for more.',
	inputSchema: objectInput({username: stringInput('The username on the account.', {maxLength: 128})}, ['username']),
	run: ({username}) => {
		fillInput('#username', username, 'username field')
		return press('button[type="submit"][form="enterUsernameForm"]', 'Continue button')
	},
})

const buildDontKnowUsername = () => actionTool({
	name: 'dont_know_username',
	description: 'Tell the site the person doesn\'t know the username on the account, on the enter-username recovery step. When the site already has the account\'s email address, it may email the username there instead of showing it. Returns the next step, often a page saying the username email was sent, or the message the page shows.',
	run: () => pressButtonWithText('I don’t know my username'),
})

const buildSignInNow = () => navigationTool({
	name: 'sign_in_now',
	description: `Go to the ${getBrandName()} sign in page from the password-updated or username-sent page, now that the account is recovered. The person then signs in with the new password there, or you call sign_in and their password manager fills it. Returns the sign in step. From any other page, use navigate instead.`,
	execute: () => pressButtonWithText('Sign in now'),
})

export const reclaimTools = {
	ReclaimAccount: [buildStartRecovery, buildUseUsernameInstead],
	ReclaimWithUsername: [buildStartRecoveryWithUsername],
	VerifySSN: [buildVerifySsn, buildRecoverWithoutSsn],
	VerifyPTIN: [buildVerifyPtin],
	SelectAccount: [buildSelectAccount, buildEmailAccountDetails],
	SelectResetMethod: [buildSendResetCode],
	EnterResetCode: [buildSubmitResetCode, buildResendResetCode],
	ChooseNewPassword: [buildChooseNewPassword],
	EnterEmail: [buildProvideRecoveryEmail, buildNoAccessToEmail],
	EnterUsername: [buildProvideRecoveryUsername, buildDontKnowUsername],
	PasswordUpdated: [buildSignInNow],
	UsernameEmailSent: [buildSignInNow],
}